import { useState, useEffect, useCallback, useRef, useMemo, ReactElement } from 'react';
import { useDispatch } from 'react-redux';
import { KitcenSinkRowItemDataItem, KitchenSinkRowItem, PregameVideo, RelatedItem } from 'store-types';

import { useVideoValidation, VideoValidationError } from 'hooks/playback';
import {
  useVideoById,
  useCurrentVideoSetter,
  useFallbackVideo,
} from 'hooks/video';

import { networkDisconnected } from 'actions/network';
import { exitFullScreenPlayer } from 'actions/hero';
import { Provider, PlaybackError } from 'app-types';

export interface PlayerFunnelRenderProps {
  videoToPlay?: KitcenSinkRowItemDataItem | RelatedItem | PregameVideo;
  videoToDisplay?: KitcenSinkRowItemDataItem | RelatedItem | PregameVideo;
  isGTAutoplay: boolean;
  row?: KitchenSinkRowItem;
  authorizedProvider?: Provider;
  loading: boolean;
  onPlaybackError: (error: unknown) => void;
  onSetResource: () => void;
}

interface PlayerFunnelProps {
  videoId?: string | null;
  rowId?: string | null;
  render?: (props: PlayerFunnelRenderProps) => ReactElement | null;
  onError?: (error: unknown) => void;
  isCarousel?: boolean;
  isDiscoveryRowVideo?: boolean;
}

const getSecondsToStart = (video: { schedule?: { videoStartDate?: number | null } } | null | undefined) => {
  return video?.schedule?.videoStartDate
    ? video.schedule.videoStartDate - Math.floor(Date.now() / 1000)
    : null;
};

const useGameTransitionVideo = (video: KitcenSinkRowItemDataItem | RelatedItem | null | undefined) => {
  const preGameVideo = video?.event?.pregame;
  const [postGameVideo] = useVideoById(video?.event?.postEvent ?? undefined);

  let gameTransitionVideo: KitcenSinkRowItemDataItem | RelatedItem | PregameVideo | undefined | null = video;
  let videoToDisplay = video ?? undefined;
  if (video) {
    if (video?.event?.status === 'pre' && (getSecondsToStart(video) as number) > 0) {
      gameTransitionVideo = preGameVideo ?? undefined;
    }
    if (video?.event?.status === 'post') {
      gameTransitionVideo = postGameVideo ?? undefined;
      videoToDisplay = postGameVideo ?? undefined;
    }
  }

  const [override, setOverride] = useState< KitcenSinkRowItemDataItem | RelatedItem | null>(null);
  useEffect(() => {
    if (!video || video?.event?.status !== 'pre') {
      return undefined;
    }

    const secondsToStart = getSecondsToStart(video);
    // Kick off timer to transition user to the live event
    const timeout = setTimeout(() => {
      setOverride(video);
    }, Math.max(0, (secondsToStart as number) * 1000));
    return () => {
      setOverride(null);
      clearTimeout(timeout);
    };
  }, [video]);

  const prevVideo = useRef(video);
  useEffect(() => {
    prevVideo.current = video;
  }, [video]);
  const isAutoplay = useMemo(() => {
    // We're only autoplaying (w/ game transitions) if we're watching the same video
    if (video && prevVideo.current?.id === video.id) {
      // and the video status had updated
      if (video && prevVideo.current && prevVideo.current?.event?.status !== video?.event?.status) {
        return true;
      }
      // or if we set the override
      if (override !== null) {
        return true;
      }
    }

    return false;
  }, [video, override]);

  return {
    videoToPlay: (override || gameTransitionVideo) ?? undefined,
    videoToDisplay,
    isAutoplay,
  };
};

const usePlayableVideo = (
  desiredVideo: KitcenSinkRowItemDataItem | RelatedItem | null | undefined,
  desiredRow: KitchenSinkRowItem | null | undefined,
  onError: (error: unknown) => void,
  isCarousel: boolean,
) => {
  const dispatch = useDispatch();
  const lastPlayable = useRef<{ video: KitcenSinkRowItemDataItem | RelatedItem | null | undefined; row: KitchenSinkRowItem | null | undefined }>({ video: null, row: null });
    const [currentIds, setCurrentIds] = useState<{ video: string | null | undefined; row: string | null | undefined }>({ video: null, row: null });

  const [currentVideo, currentRow] = useVideoById(
    currentIds.video ?? undefined,
    currentIds.row ?? undefined,
  );

  const [fallbackVideo, fallbackRow] = useFallbackVideo();

  // Falling back to current video as don't want to trigger changes to
  // isValid or authorizedProvider by passing in null to useVideoValidation
  // since the user should still be watching their previously selected stream
  // even after navigating to a page with no video in the query parameters
  const videoToValidate =
    desiredVideo || currentVideo || lastPlayable.current.video;
  const rowToValidate = desiredRow || currentRow || lastPlayable.current.row;

  const handleError = (error: unknown | VideoValidationError) => {
    if ((error as VideoValidationError)?.type === 'authN') {
      if (isCarousel) {
        dispatch(exitFullScreenPlayer());
      }
      if (currentVideo && videoToValidate && currentVideo?.id === videoToValidate?.id) {
        setCurrentIds({ video: fallbackVideo?.id, row: fallbackRow?.id });
        return;
      }
    }
    onError(error);
  };

  // Don't need to revalidate if we're attempting to play the currently playing video
  const { isValid, authorizedProvider, loading } = useVideoValidation(
    videoToValidate,
    handleError,
    isCarousel,
  );

  let playableVideo: KitcenSinkRowItemDataItem | RelatedItem | null | undefined = null;
  let playableRow: KitchenSinkRowItem | null | undefined = null;

  if (isValid && !loading) {
    playableVideo = videoToValidate;
    playableRow = rowToValidate;
  } else if (currentVideo) {
    playableVideo = currentVideo;
    playableRow = currentRow;
  }

  if (!playableVideo) {
    // Fallback for no video
    if (fallbackVideo && !isCarousel) {
      playableVideo = fallbackVideo;
      playableRow = fallbackRow;
    }
  }

  useEffect(() => {
    setCurrentIds({ video: playableVideo?.id, row: playableRow?.id });
    lastPlayable.current = { video: playableVideo, row: playableRow };
  }, [playableVideo, playableRow]);

  return {
    playableVideo,
    playableRow,
    authorizedProvider,
    loading,
  };
};

const PlayerFunnel = ({
  videoId = null,
  rowId = null,
  render = () => null,
  onError = () => null,
  isCarousel = false,
  isDiscoveryRowVideo = false,
}: PlayerFunnelProps) => {
  const dispatch = useDispatch();
  const [video, row] = useVideoById(videoId, rowId, isDiscoveryRowVideo);

  const {
    playableVideo,
    playableRow,
    authorizedProvider,
    loading,
  } = usePlayableVideo(video, row, onError, isCarousel);

  const {
    videoToPlay,
    videoToDisplay,
    isAutoplay: isGTAutoplay,
  } = useGameTransitionVideo(playableVideo);

  const setCurrentVideo = useCurrentVideoSetter();
  const onSetResource = useCallback(() => {
    setCurrentVideo(
      videoToDisplay,
      videoToDisplay?.id,
      playableRow?.id,
      isDiscoveryRowVideo,
    );
  }, [videoToDisplay, playableRow, setCurrentVideo, isDiscoveryRowVideo]);

  const onPlaybackError = (error: unknown) => {
    if ((error as PlaybackError)?.detail?.error?.code === '3002') {
      dispatch(networkDisconnected());
      onError({ type: 'network', error });
    } else {
      onError({ type: 'playback', error });
    }
  };

  return render({
    videoToPlay,
    videoToDisplay,
    isGTAutoplay,
    row: playableRow ?? undefined,
    authorizedProvider,
    loading,
    onPlaybackError,
    onSetResource,
  });
};

export default PlayerFunnel;
